/* Shared formatting/month-range helpers for the FTE Report (Summary + Details tabs). */

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** "2026-04", "2026-05", ... inclusive of both ends — same key shape
 *  actualsVsForecastByDateRangeUtils.js's colsFromKeys expects ("YYYY-MM"). */
export function monthKeysInRange(startMonth, startYear, endMonth, endYear) {
  const keys = [];
  let m = startMonth;
  let y = startYear;
  while (y < endYear || (y === endYear && m <= endMonth)) {
    keys.push(`${y}-${String(m).padStart(2, "0")}`);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return keys;
}

/** Current month through 11 months ahead (12 months) — Details lists future hires only. */
export function futureDateRange() {
  const now = new Date();
  const startMonth = now.getMonth() + 1;
  const startYear = now.getFullYear();
  const endTotal = startYear * 12 + (startMonth - 1) + 11;
  return { startMonth, startYear, endMonth: (endTotal % 12) + 1, endYear: Math.floor(endTotal / 12) };
}

export function monthKeyLabel(key) {
  const [y, m] = key.split("-").map(Number);
  return `${MONTH_LABELS[m - 1]} ${y}`;
}

export function fmtNumber(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-US", { maximumFractionDigits: 1 });
}

export function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

/** The reports API may or may not wrap its payload in { data } — accept both; null when the
 *  response isn't a usable FTE payload (error object, empty body). */
export function unwrapFteResponse(res) {
  const body = res?.data?.columns ? res.data : res;
  return Array.isArray(body?.columns) ? body : null;
}

/** API summary payload -> what Summary renders. Each section keeps the API's LINE objects
 *  ({ months, hours, average }) so Totals / Grand Totals / Averages are the server's numbers. */
export function mapFteSummary(body) {
  const section = (s) => ({
    employees: s?.employees,
    iwo: s?.iwoEmployees,
    subcontractors: s?.subcontractors,
    totals: s?.totals,
  });
  return {
    monthKeys: body.columns,
    actualMonths: body.actualMonths || [],
    availableHours: body.availableHours || {},
    availableHoursAverage: body.availableHoursAverage,
    existing: section(body.existing),
    tbd: section(body.tbd),
    grandTotals: body.grandTotals,
  };
}

const DETAILS_CATEGORIES = [
  ["Employees", "employees"],
  ["IWO Employees", "iwoEmployees"],
  ["Subcontractors", "subcontractors"],
];

/** API details payload -> { monthKeys, sections: [{ category, rows: [{ index, months: {key: slot|null} }] }] }. */
export function mapFteDetails(body) {
  return {
    monthKeys: body.columns,
    // Months with actualized data; monthTypes ("Actual" | "Forecast" per column) is the fallback.
    actualMonths:
      body.actualMonths ||
      body.columns.filter((m) => body.monthTypes?.[m] === "Actual"),
    sections: DETAILS_CATEGORIES.map(([category, field]) => ({
      category,
      rows: body[field] || [],
    })),
  };
}

export { MONTH_LABELS };
